import React, { useCallback, useEffect, useState } from "react";
import { saveAs } from "file-saver";
import ColorThief from "colorthief";
import chroma from "chroma-js";
import { darkOrLightColor } from "../../../helpers/darkOrLightColor";
import {
  colors_from_image_wrapper,
  result_status,
  extracted_colors,
  color_swatch,
  color_code,
  palette_actions,
  primary_action_btn,
  action_btn,
  toast,
  error_message,
} from "../../../styles/modules/palette_from_image.module.css";

const TEXTS = {
  es: {
    extractedColors: "6 colores extraídos",
    extracting: "Analizando la imagen…",
    extractError: "No pudimos detectar los colores de esta imagen.",
    copyColor: "Copiar color",
    copyAll: "Copiar todos los HEX",
    copyCSS: "Copiar CSS",
    download: "Descargar imagen y paleta",
    downloading: "Preparando descarga…",
    downloaded: "Descarga preparada",
    copied: "Copiado",
    copyError: "No se pudo copiar. Inténtalo de nuevo.",
    downloadError: "No pudimos crear la descarga.",
    paletteTitle: "Paleta extraída",
  },
  en: {
    extractedColors: "6 extracted colors",
    extracting: "Analyzing image…",
    extractError: "We could not detect colors in this image.",
    copyColor: "Copy color",
    copyAll: "Copy all HEX codes",
    copyCSS: "Copy CSS",
    download: "Download image and palette",
    downloading: "Preparing download…",
    downloaded: "Download ready",
    copied: "Copied",
    copyError: "Could not copy. Please try again.",
    downloadError: "We could not create the download.",
    paletteTitle: "Extracted palette",
  },
  pt: {
    extractedColors: "6 cores extraídas",
    extracting: "Analisando a imagem…",
    extractError: "Não foi possível detectar as cores desta imagem.",
    copyColor: "Copiar cor",
    copyAll: "Copiar todos os HEX",
    copyCSS: "Copiar CSS",
    download: "Baixar imagem e paleta",
    downloading: "Preparando download…",
    downloaded: "Download pronto",
    copied: "Copiado",
    copyError: "Não foi possível copiar. Tente novamente.",
    downloadError: "Não foi possível criar o download.",
    paletteTitle: "Paleta extraída",
  },
  fr: {
    extractedColors: "6 couleurs extraites",
    extracting: "Analyse de l’image…",
    extractError: "Impossible de détecter les couleurs de cette image.",
    copyColor: "Copier la couleur",
    copyAll: "Copier tous les codes HEX",
    copyCSS: "Copier le CSS",
    download: "Télécharger l’image et la palette",
    downloading: "Préparation du téléchargement…",
    downloaded: "Téléchargement prêt",
    copied: "Copié",
    copyError: "Impossible de copier. Réessayez.",
    downloadError: "Impossible de créer le téléchargement.",
    paletteTitle: "Palette extraite",
  },
  de: {
    extractedColors: "6 extrahierte Farben",
    extracting: "Bild wird analysiert…",
    extractError: "Die Farben dieses Bildes konnten nicht erkannt werden.",
    copyColor: "Farbe kopieren",
    copyAll: "Alle HEX-Codes kopieren",
    copyCSS: "CSS kopieren",
    download: "Bild und Palette herunterladen",
    downloading: "Download wird vorbereitet…",
    downloaded: "Download ist bereit",
    copied: "Kopiert",
    copyError: "Kopieren fehlgeschlagen. Versuche es erneut.",
    downloadError: "Der Download konnte nicht erstellt werden.",
    paletteTitle: "Extrahierte Palette",
  },
};

const loadImage = src =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const safeDownloadName = name => {
  const withoutExtension = (name || "image").replace(/\.[^/.]+$/, "");
  const normalized = withoutExtension
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return `${normalized || "image"}-paleta.png`;
};

function ColorsFromImage({ images = [], locale }) {
  const texts = TEXTS[locale] || TEXTS.es;
  const [palette, setPalette] = useState([]);
  const [isExtracting, setIsExtracting] = useState(true);
  const [extractError, setExtractError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    if (!images.length) {
      setPalette([]);
      setIsExtracting(false);
      return undefined;
    }

    let isCurrent = true;
    const colorThief = new ColorThief();
    const image = document.createElement("img");
    setPalette([]);
    setExtractError("");
    setIsExtracting(true);

    image.onload = () => {
      if (!isCurrent) return;

      try {
        const colors = colorThief.getPalette(image, 6);
        const formattedPalette = colors.map(color =>
          chroma.rgb(color[0], color[1], color[2]).hex().toUpperCase()
        );
        setPalette(formattedPalette);
      } catch (error) {
        setExtractError(texts.extractError);
      } finally {
        setIsExtracting(false);
      }
    };

    image.onerror = () => {
      if (!isCurrent) return;
      setExtractError(texts.extractError);
      setIsExtracting(false);
    };

    image.src = images[0].src;

    return () => {
      isCurrent = false;
    };
  }, [images, texts.extractError]);

  const displayToast = useCallback(message => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(""), 2200);
  }, []);

  const copyText = useCallback(
    async (value, successMessage = texts.copied) => {
      try {
        await navigator.clipboard.writeText(value);
        displayToast(successMessage);
      } catch (error) {
        displayToast(texts.copyError);
      }
    },
    [displayToast, texts.copied, texts.copyError]
  );

  const copyCSSVariables = useCallback(() => {
    const css = `:root {\n${palette
      .map((color, index) => `  --color-${index + 1}: ${color};`)
      .join("\n")}\n}`;
    copyText(css);
  }, [copyText, palette]);

  const downloadPalette = useCallback(async () => {
    if (!palette.length || !images.length) return;

    setIsDownloading(true);

    try {
      const sourceImage = await loadImage(images[0].src);
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const width = 1200;
      const padding = 48;
      const headerHeight = 88;
      const imageMaxWidth = width - padding * 2;
      const imageMaxHeight = 720;
      const imageScale = Math.min(
        imageMaxWidth / sourceImage.naturalWidth,
        imageMaxHeight / sourceImage.naturalHeight,
        1
      );
      const imageWidth = Math.round(sourceImage.naturalWidth * imageScale);
      const imageHeight = Math.round(sourceImage.naturalHeight * imageScale);
      const paletteGap = 32;
      const paletteHeight = 170;

      canvas.width = width;
      canvas.height =
        headerHeight + imageHeight + paletteGap + paletteHeight + padding;

      context.fillStyle = "#F8FAFC";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = "#1E293B";
      context.font = "700 32px Inter, Arial, sans-serif";
      context.fillText(texts.paletteTitle, padding, 52);
      context.fillStyle = "#64748B";
      context.font = "500 18px Inter, Arial, sans-serif";
      context.textAlign = "right";
      context.fillText("paletadecolores.online", width - padding, 50);
      context.textAlign = "left";

      const imageX = Math.round((width - imageWidth) / 2);
      context.fillStyle = "#FFFFFF";
      context.fillRect(imageX, headerHeight, imageWidth, imageHeight);
      context.drawImage(
        sourceImage,
        imageX,
        headerHeight,
        imageWidth,
        imageHeight
      );

      const swatchY = headerHeight + imageHeight + paletteGap;
      const swatchWidth = imageMaxWidth / palette.length;

      palette.forEach((color, index) => {
        const swatchX = padding + swatchWidth * index;
        context.fillStyle = color;
        context.fillRect(swatchX, swatchY, swatchWidth + 1, paletteHeight);
        context.fillStyle = darkOrLightColor(color);
        context.font = "700 20px Inter, Arial, sans-serif";
        context.textAlign = "center";
        context.fillText(
          color,
          swatchX + swatchWidth / 2,
          swatchY + paletteHeight - 24
        );
      });

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(result => {
          if (result) resolve(result);
          else reject(new Error("Canvas export failed"));
        }, "image/png");
      });

      saveAs(blob, safeDownloadName(images[0].name));
      displayToast(texts.downloaded);
    } catch (error) {
      displayToast(texts.downloadError);
    } finally {
      setIsDownloading(false);
    }
  }, [
    displayToast,
    images,
    palette,
    texts.downloaded,
    texts.downloadError,
    texts.paletteTitle,
  ]);

  return (
    <section
      className={colors_from_image_wrapper}
      aria-labelledby="extracted-colors-title"
    >
      <div className={result_status} aria-live="polite">
        <h2 id="extracted-colors-title">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <circle cx="9" cy="9" r="1" />
            <circle cx="15" cy="9" r="1" />
            <circle cx="9" cy="15" r="1" />
            <path d="M15 14h.01" />
          </svg>
          {isExtracting ? texts.extracting : texts.extractedColors}
        </h2>
      </div>

      {extractError ? (
        <p className={error_message} role="alert">
          {extractError}
        </p>
      ) : null}

      {!isExtracting && !extractError ? (
        <>
          <div className={extracted_colors}>
            {palette.map((color, index) => (
              <button
                type="button"
                key={`${color}-${index}`}
                className={color_swatch}
                style={{
                  backgroundColor: color,
                  color: darkOrLightColor(color),
                }}
                onClick={() => copyText(color, `${texts.copied}: ${color}`)}
                aria-label={`${texts.copyColor} ${color}`}
              >
                <span className={color_code}>{color}</span>
              </button>
            ))}
          </div>

          <div className={palette_actions}>
            <button
              type="button"
              className={primary_action_btn}
              onClick={() => copyText(palette.join(", "))}
            >
              {texts.copyAll}
            </button>
            <button
              type="button"
              className={action_btn}
              onClick={copyCSSVariables}
            >
              {texts.copyCSS}
            </button>
            <button
              type="button"
              className={action_btn}
              onClick={downloadPalette}
              disabled={isDownloading}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
                <path d="M5 19h14" />
              </svg>
              {isDownloading ? texts.downloading : texts.download}
            </button>
          </div>
        </>
      ) : null}

      {toastMessage ? (
        <div className={toast} role="status">
          {toastMessage}
        </div>
      ) : null}
    </section>
  );
}

export default ColorsFromImage;
